import {
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PersonListCard,
  PaneResponsiveCardGrid,
  DashboardSectionSkeleton,
  DashboardSectionTitle,
  Button,
  formatClientDateTimeMedium,
  interviewerSessionHref,
  employerRecentApplicantToPersonModel,
  getHrefWithThreadPreservation,
} from '@yeshuman/sdk'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Video } from 'lucide-react'

import {
  useInterviewerCandidates,
  type InterviewerCandidateRow,
  type InterviewerNextInterview,
} from '../../hooks/use-interviewer-candidates'

type BookingRow = {
  key: string
  candidate: InterviewerCandidateRow
  interview: InterviewerNextInterview
  upcoming: boolean
}

function bookingHref(row: BookingRow) {
  const person = employerRecentApplicantToPersonModel(row.candidate)
  return getHrefWithThreadPreservation(
    interviewerSessionHref({
      profileId: row.candidate.profile.id,
      opportunityId: row.candidate.opportunity_id,
      label: person.name,
      interviewId: row.interview.id,
      liveSessionId: row.candidate.live_session_id,
    }),
  )
}

export function InterviewerInterviewsPage() {
  const navigate = useNavigate()
  const { data: candidates = [], isLoading, error } = useInterviewerCandidates()

  const { upcoming, past } = useMemo(() => {
    const now = Date.now()
    const booked: BookingRow[] = []
    for (const candidate of candidates) {
      for (const interview of candidate.interviews ?? []) {
        booked.push({
          key: `${candidate.id}:${interview.id}`,
          candidate,
          interview,
          upcoming: new Date(interview.scheduled_start).getTime() >= now,
        })
      }
    }
    booked.sort((a, b) =>
      a.interview.scheduled_start.localeCompare(b.interview.scheduled_start),
    )
    return {
      upcoming: booked.filter((row) => row.upcoming),
      past: booked.filter((row) => !row.upcoming).reverse(),
    }
  }, [candidates])

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        title="Interviews"
        description="Scheduled times. Interview now when you are ready."
      />

      {isLoading ? (
        <DashboardSectionSkeleton cards={4} grid />
      ) : error ? (
        <p className="text-sm text-muted-foreground">Unable to load interviews.</p>
      ) : upcoming.length === 0 && past.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No interviews booked yet. Schedule one from Candidates.
        </p>
      ) : (
        <div className="space-y-8">
          <section className="space-y-3">
            <DashboardSectionTitle id="interviews-upcoming" title="Upcoming" />
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing scheduled.</p>
            ) : (
              <PaneResponsiveCardGrid>
                {upcoming.map((row) => {
                  const person = employerRecentApplicantToPersonModel(row.candidate)
                  return (
                    <PersonListCard
                      key={row.key}
                      variant="compact"
                      person={person}
                      meta={`${row.interview.round_name} · ${formatClientDateTimeMedium(row.interview.scheduled_start)}`}
                      footer={
                        <Button type="button" size="sm" onClick={() => navigate(bookingHref(row))}>
                          <Video className="size-3.5" aria-hidden />
                          Interview now
                        </Button>
                      }
                    />
                  )
                })}
              </PaneResponsiveCardGrid>
            )}
          </section>
          {past.length > 0 ? (
            <section className="space-y-3">
              <DashboardSectionTitle id="interviews-past" title="Past" />
              <PaneResponsiveCardGrid>
                {past.map((row) => {
                  const person = employerRecentApplicantToPersonModel(row.candidate)
                  return (
                    <PersonListCard
                      key={row.key}
                      variant="compact"
                      person={person}
                      meta={`${row.interview.round_name} · ${formatClientDateTimeMedium(row.interview.scheduled_start)}`}
                    />
                  )
                })}
              </PaneResponsiveCardGrid>
            </section>
          ) : null}
        </div>
      )}
    </PageContainer>
  )
}
